/**
 * @file useColumnFilters.js
 * @version 2.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-26
 * @modified 2026-10-23
 * @description Right-click a column header, tick the values you want to keep.
 *
 *              The search headers had grown a filter for every question anyone
 *              had ever asked of a table — status, supplier, type, rep — and
 *              each took a slot whether or not it was the question you had
 *              today. Meanwhile the search box, which is what people actually
 *              reach for, was squeezed into whatever was left.
 *
 *              v1 filtered the page in the browser. Cheap to build and wrong in
 *              the way that matters: it was a lens over the rows you happened to
 *              be looking at, the row count and pagination disagreed with it,
 *              and it had to clear itself whenever the data changed or a stale
 *              value would hide rows for a reason nobody could see.
 *
 *              v2 sends the selection to the server. The filter narrows the
 *              whole result set, the counts and page count reflect it, and it
 *              survives searching, re-filtering and paginating — because it is
 *              part of the query rather than something applied after it.
 *
 *              Options come from server-computed facets: the values present
 *              across the entire result set, with counts. Each column's facets
 *              are computed WITHOUT its own filter applied, so once you tick
 *              "Draft" the Status list still offers the others — otherwise
 *              widening a selection means clearing it and starting again.
 *
 *              Usage:
 *                const cols = useColumnFilters(rows, DEFS, {
 *                  facets: pagination?.facets,
 *                  storageKey: 'searchPOs.columnFilters',
 *                  onChange: () => setPage(1)
 *                });
 *                // include cols.queryValue in the search request
 *                <th {...cols.headerProps('status')}>Status</th>
 *                <ColumnFilterMenu cols={cols} />
 *
 * 2.2.0 (2026-10-05): `optionLabel(key, value)` — the words a stored value is
 *              shown in (a bill's Authorised reads "Approved"). Display only:
 *              the value ticked and sent to the server is unchanged.
 *
 * 2.3.0 (2026-10-14, Cam): a definition or a section may declare `values` —
 *              every value the column can hold, in the order to show them.
 *              They are always offered, a value no row has yet with a count
 *              of 0, so a status nobody is in today (Draft Invoice, Partially
 *              Billed) can still be looked for; a value the facets bring that
 *              is not listed is still offered, after them.
 *
 * 2.4.0 (2026-10-23): `replace(next)` sets the whole selection at once — for a
 *              screen whose columns change under it (the Aged Debt report's
 *              ageing switch drops the filters of columns it no longer shows).
 */

import { useState, useMemo, useCallback } from 'react';
import usePersistedState from './usePersistedState';
import { optionsFor as optionsFromRows, applyFilters } from '../utils/columnFilter';

const CLOSED = { key: null, anchor: null };

export default function useColumnFilters(rows = [], definitions = {}, options = {}) {
  const { facets = null, storageKey = null, onChange = null, optionLabel = null } = options;

  // Plain arrays rather than Sets: this goes through sessionStorage and into a
  // query string, and a Set survives neither.
  const persisted = usePersistedState(storageKey || 'columnFilters.unnamed', {}, { merge: false });
  const local = useState({});
  const [selected, setSelected] = storageKey ? persisted : local;

  const [menu, setMenu] = useState(CLOSED);

  const apply = useCallback((next) => {
    setSelected(next);
    // The caller re-runs its search, and resets to page 1 — staying on page 7
    // of a result set that just shrank to two pages shows nothing at all.
    if (onChange) onChange(next);
  }, [setSelected, onChange]);

  /**
   * Options for one column.
   *
   * Server facets where the screen provides them — the values across the whole
   * result set. Otherwise derived from the rows on screen, which is the older,
   * page-scoped behaviour: weaker, but a working filter over one page beats an
   * empty menu on a screen whose search endpoint has not been converted yet.
   * `hasFacets` is what the menu reads to describe its own scope honestly.
   */
  // `values` declared on a definition or one of its sections, by filter key.
  const fixedValues = useMemo(() => {
    const out = {};
    for (const [key, def] of Object.entries(definitions || {})) {
      if (Array.isArray(def?.values)) out[key] = def.values;
      for (const section of def?.sections || []) {
        if (Array.isArray(section?.values)) out[section.key] = section.values;
      }
    }
    return out;
  }, [definitions]);

  const optionsFor = useCallback((key) => {
    // Every declared value first, in its own order (0 when no row has it),
    // then whatever else is there.
    const withFixed = (list) => {
      const fixed = fixedValues[key];
      if (!fixed) return list;
      const byValue = new Map(list.map((o) => [o.value, o]));
      const listed = fixed.map((v) => byValue.get(String(v)) || { value: String(v), count: 0 });
      const known = new Set(fixed.map(String));
      return [...listed, ...list.filter((o) => !known.has(o.value))];
    };
    const labelled = (list) => (optionLabel
      ? list.map((o) => {
        const label = optionLabel(key, o.value);
        return label && label !== o.value ? { ...o, label } : o;
      })
      : list);
    if (facets) return labelled(withFixed((facets[key] || []).map((f) => ({ value: String(f.value), count: f.count }))));
    const definition = definitions[key];
    return labelled(definition?.value ? optionsFromRows(rows, definition) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facets, rows, definitions, optionLabel, fixedValues]);

  /**
   * The sections a header's menu offers.
   *
   * Most columns are one question with one list of answers, and those get a
   * single implicit section so nothing about them changes. A few are several
   * questions wearing one header: a customer order's Status cell is a row of
   * icons saying whether it is on a PO, sent, confirmed, planned, delivered
   * and priced — six independent facts that used to need six dropdowns in the
   * filter row to narrow. A definition can therefore declare `sections`, each
   * naming its OWN filter key, and the menu shows them stacked and collapsible.
   *
   * Every section key must exist in the route's server-side whitelist, or that
   * section offers no values and narrows nothing.
   */
  const sectionsFor = useCallback((key) => {
    const definition = definitions[key];
    if (definition?.sections?.length) {
      return definition.sections.map((section, i) => ({
        defaultOpen: i === 0,   // the first is open; the rest are a click away
        ...section
      }));
    }
    return [{ key, label: definition?.label || key, defaultOpen: true, implicit: true }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitions]);

  /**
   * Whether a HEADER is filtered — true when any of its sections is, so a
   * sectioned header still marks itself when the narrowing is two sections
   * down and out of sight.
   */
  const isFiltered = useCallback(
    (key) => sectionsFor(key).some((section) => Array.isArray(selected[section.key])),
    [selected, sectionsFor]
  );

  const toggle = useCallback((key, value) => {
    // An untouched column starts from everything, so the first click subtracts.
    // Starting from empty blanks the table on the first tick, which reads as a
    // broken control.
    const base = selected[key] || optionsFor(key).map((o) => o.value);
    const next = base.includes(value)
      ? base.filter((v) => v !== value)
      : [...base, value];
    apply({ ...selected, [key]: next });
  }, [selected, optionsFor, apply]);

  const selectAll = useCallback((key) => {
    // Everything ticked is the absence of a filter, so the entry is removed
    // rather than kept as a list that would have to be maintained as the result
    // set changes underneath it.
    const next = { ...selected };
    delete next[key];
    apply(next);
  }, [selected, apply]);

  const clearAll = useCallback((key) => {
    // Nothing ticked is a real state — an empty list, not a missing one.
    apply({ ...selected, [key]: [] });
  }, [selected, apply]);

  const clearEverything = useCallback(() => apply({}), [apply]);

  const openMenu = useCallback((key, anchor) => setMenu({ key, anchor }), []);
  const closeMenu = useCallback(() => setMenu(CLOSED), []);

  /**
   * Spread onto a <th>.
   *
   * stopPropagation matters: these tables put a context menu on the <table>
   * itself, and without it a right-click on a header opens both.
   */
  const headerProps = useCallback((key) => ({
    onContextMenu: (e) => {
      if (!definitions[key]) return;
      e.preventDefault();
      e.stopPropagation();
      openMenu(key, e.currentTarget);
    },
    className: isFiltered(key) ? 'col-filtered' : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [definitions, isFiltered, openMenu]);

  const activeCount = Object.values(selected).filter(Array.isArray).length;

  /** What to send with the search. Empty when nothing is filtered. */
  const queryValue = useMemo(
    () => (activeCount ? JSON.stringify(selected) : ''),
    [selected, activeCount]
  );

  /**
   * In server mode the rows arrive already filtered, so they pass through. In
   * the page-scoped fallback they are filtered here, using the same rules the
   * server implements — see utils/columnFilter.js and its check script.
   */
  const visibleRows = useMemo(() => {
    if (facets) return rows;
    const asSets = Object.fromEntries(
      Object.entries(selected)
        .filter(([, v]) => Array.isArray(v))
        .map(([k, v]) => [k, new Set(v)])
    );
    return applyFilters(rows, definitions, asSets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facets, rows, selected, definitions]);

  return {
    rows: visibleRows,
    allRows: rows,
    definitions,
    selected,
    menu,
    optionsFor,
    sectionsFor,
    isFiltered,
    toggle,
    selectAll,
    clearAll,
    clearEverything,
    replace: apply,
    openMenu,
    closeMenu,
    headerProps,
    activeCount,
    queryValue,
    hasFacets: !!facets,
    hiddenCount: facets ? 0 : rows.length - visibleRows.length
  };
}
